import React, { useState } from 'react';
import Select from 'react-select';
import { CircularProgress, Button, Grid, Typography, useTheme } from '@mui/material';
import {
  JobsFieldSet,
  ShiftDropoffsFieldSet,
  ShiftsFieldSet,
  RigsFieldSet,
  RobotsFieldSet,
  TeamFieldSet,
} from '@rogoag/airtable/crm';
import { LocalAtRecord } from '../../db';
import { addDays, generateScheduledKey } from '../../utils';
import ShiftService from '../../services/ShiftService';
import CreateShiftFormFields from './CreateShiftFormFields';
import { createShiftInAt } from './helpers/shift';
import { alertWarn } from '../../alertDispatcher';

interface DestinationShiftOption {
  label: string;
  value: string;
}

interface ShiftRolloverFormProps {
  availableShifts: LocalAtRecord<ShiftsFieldSet>[];
  rolloverFromShift: LocalAtRecord<ShiftsFieldSet>;
  shiftEntities: (LocalAtRecord<JobsFieldSet> | LocalAtRecord<ShiftDropoffsFieldSet>)[];
  onCompletion?: () => Promise<void>;
  allRobots: LocalAtRecord<RobotsFieldSet>[];
  allOperators: LocalAtRecord<TeamFieldSet>[];
  allRigs: LocalAtRecord<RigsFieldSet>[];
}

const createNewShiftOption: DestinationShiftOption = {
  label: '➕ Create new shift',
  value: '__create_new__',
};

const ShiftRolloverForm: React.FC<ShiftRolloverFormProps> = ({
  availableShifts,
  rolloverFromShift,
  shiftEntities,
  onCompletion,
  allRobots,
  allOperators,
  allRigs,
}) => {
  const destinationShiftOptions = availableShifts
    .map((shift) => {
      const robot = shift.get('Robot')?.[0];
      const schedDate = shift.get('Sched Date');
      const operatorName = shift.get('Operator Name')?.[0];
      const rig = shift.get('Rig #') || '';

      if (!robot || !schedDate || !operatorName || !rig) {
        return false;
      }

      return {
        label: generateScheduledKey(schedDate!, robot, operatorName, rig),
        value: shift.id,
      };
    })
    .filter((option) => option !== false);

  const [destinationShiftId, setDestinationShiftId] = useState<DestinationShiftOption | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const options = [...destinationShiftOptions, createNewShiftOption];

  const [newShiftSchedDate, setNewShiftSchedDate] = useState(addDays(rolloverFromShift.get('Sched Date') || '', 1));
  const [newShiftRobot, setNewShiftRobot] = useState(rolloverFromShift.get('Scheduled Robot')?.toString() || '');
  const [newShiftRig, setNewShiftRig] = useState(rolloverFromShift.get('Schedule Rig')?.toString() || '');
  const [newShiftOperator, setNewShiftOperator] = useState(rolloverFromShift.get('Operator')?.toString() || '');

  const rollingOverToNewShift = destinationShiftId === createNewShiftOption;

  const onRollover = async () => {
    setMessage('');
    setLoading(true);

    if (!destinationShiftId) {
      setMessage('No destination shift ID found');
      setLoading(false);

      return;
    }

    if (!rolloverFromShift) {
      setMessage(`Abort, no rollover-from-shift`);
      setLoading(false);

      return;
    }

    // We are only supporting rolling over jobs now
    const jobsToRollover = shiftEntities.filter(
      (entity): entity is LocalAtRecord<JobsFieldSet> => entity.table === 'Jobs',
    );

    if (jobsToRollover.length === 0) {
      setMessage(`Abort, no jobs to rollover`);
      setLoading(false);

      return;
    }

    let destinationShift: LocalAtRecord<ShiftsFieldSet> | undefined | void;

    if (rollingOverToNewShift && newShiftSchedDate && newShiftRobot && newShiftOperator && newShiftRig) {
      if (
        availableShifts.find((shift: LocalAtRecord<ShiftsFieldSet>) => {
          return (
            shift.get('Sched Date') === newShiftSchedDate &&
            shift.get('Scheduled Robot')?.toString() === newShiftRobot &&
            shift.get('Schedule Rig')?.toString() === newShiftRig &&
            shift.get('Operator')?.toString() === newShiftOperator
          );
        })
      ) {
        alertWarn('Shift with these parameters already exists - choose from the list');
        setLoading(false);

        return;
      }

      destinationShift = await createShiftInAt(newShiftSchedDate, newShiftRobot, newShiftOperator, newShiftRig);
    } else {
      destinationShift = availableShifts.find((shift) => shift.id === destinationShiftId.value);
    }

    if (!destinationShift) {
      setMessage(`Abort, no destination-shift`);
      setLoading(false);

      return;
    }

    const shiftService = new ShiftService();
    await shiftService.rollOverJobs(rolloverFromShift, destinationShift, jobsToRollover);

    if (onCompletion) {
      await onCompletion();
    }

    setLoading(false);
  };

  const shouldDisableRollover = (): boolean => {
    if (loading) {
      return true;
    }

    if (!destinationShiftId) {
      return true;
    }

    if (rollingOverToNewShift) {
      if (!newShiftSchedDate || !newShiftRobot || !newShiftOperator || !newShiftRig) {
        return true;
      }
    }

    return false;
  };

  return (
    <Grid container spacing={3} direction="column" size={12}>
      <Grid>
        <Typography variant="h6" align="center">
          Select Destination Shift
        </Typography>
      </Grid>
      <Grid>
        <Select
          value={destinationShiftId}
          onChange={(option) => setDestinationShiftId(option as DestinationShiftOption)}
          options={options}
          isSearchable
          placeholder="Choose a shift..."
          styles={{
            menu: (provided) => ({ ...provided, zIndex: 9999 }),
          }}
        />
      </Grid>
      {rollingOverToNewShift && (
        <CreateShiftFormFields
          schedDate={newShiftSchedDate}
          robot={newShiftRobot}
          operator={newShiftOperator}
          rig={newShiftRig}
          allRobots={allRobots}
          allOperators={allOperators}
          allRigs={allRigs}
          onSchedDateChange={(newSchedDate) => setNewShiftSchedDate(newSchedDate)}
          onRobotChange={(newRobot) => setNewShiftRobot(newRobot)}
          onOperatorChange={(newOperator) => setNewShiftOperator(newOperator)}
          onRigChange={(newRig) => setNewShiftRig(newRig)}
        />
      )}
      <Grid>
        <Button variant="contained" color="primary" fullWidth onClick={onRollover} disabled={shouldDisableRollover()}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Rollover'}
        </Button>
      </Grid>
      <Grid>
        <Typography variant="caption" color="error">
          {message}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default ShiftRolloverForm;
